import { callFunction } from '../utils/apiClient';

export interface PlanOption {
  id: 'free' | 'plus' | 'teams';
  label: string;
  priceMonthlyUsd: number;
  perSeat?: boolean;
  tagline: string;
  features: string[];
  cta: string;
  highlighted?: boolean;
}

/** PUBLIC — pricing catalog for the pricing page (viewable logged-out). */
export const getPlans = () => callFunction<{ plans: PlanOption[] }>('getPlans', {}, { requireAuth: false });

interface CheckoutResult { configured: boolean; url?: string; message?: string }

/** Start a subscription upgrade. Returns a Stripe Checkout URL once payments are configured. */
export const createCheckoutSession = (planId: string) =>
  callFunction<CheckoutResult>('createCheckoutSession', { planId });

/** Start a one-time AI-credit top-up for a pack. */
export const createTopUpCheckoutSession = (packId: string) =>
  callFunction<CheckoutResult>('createTopUpCheckoutSession', { packId });

/** Open the Stripe Customer Portal (manage card / invoices / cancel) for a paying user. */
export const createBillingPortalSession = () =>
  callFunction<CheckoutResult>('createBillingPortalSession', {});

/**
 * EMULATOR-ONLY — simulate a completed purchase so the entitlement flow is testable on
 * localhost (Stripe webhooks can't reach the emulator). The backend route is hard-gated to the
 * Functions emulator, so calling this anywhere but local dev just throws permission-denied.
 */
export const devSimulatePurchase = (args: { planId?: string; packId?: string }) =>
  callFunction<{ simulated: boolean }>('devSimulatePurchase', args);

/** True when the app is running against the local dev host. */
const isLocalDev = () =>
  typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

/** Begin the billing portal and redirect; returns a message if not available. */
export async function openBillingPortal(): Promise<{ ok: boolean; message?: string }> {
  const res = await createBillingPortalSession();
  if (res.configured && res.url) { window.location.assign(res.url); return { ok: true }; }
  return { ok: false, message: res.message || 'Billing management isn’t available yet.' };
}

/**
 * Begin a Stripe Checkout and redirect the browser to it. Returns a friendly message when
 * payments aren't configured yet (so the UI can show "coming soon" instead of erroring).
 */
export async function startCheckout(kind: 'subscription' | 'topup', id: string): Promise<{ ok: boolean; message?: string }> {
  const res = kind === 'topup' ? await createTopUpCheckoutSession(id) : await createCheckoutSession(id);
  if (res.configured && res.url) { window.location.assign(res.url); return { ok: true }; }
  // Local dev: Stripe can't reach the emulator, so simulate the purchase and land on the same
  // success URL Stripe would have used — the wallet page then refreshes and shows the new credits.
  if (isLocalDev()) {
    try {
      await devSimulatePurchase(kind === 'topup' ? { packId: id } : { planId: id });
      window.location.assign(kind === 'topup' ? `/wallet?toppedup=${id}` : `/wallet?upgraded=${id}`);
      return { ok: true, message: 'Simulated locally — wallet updated.' };
    } catch { /* not the emulator / not permitted — fall through to the message */ }
  }
  return { ok: false, message: res.message || 'Checkout isn’t available yet.' };
}
